import React from 'react';
import useReverseGeocode from '../../hooks/useReverseGeocode';
import { PinIcon } from './Icons';
import type { MapDrawing } from '../../types';

type Props = {
  lat?: number;
  lng?: number;
  initialText?: string; // if provided, show immediately while resolving
  icon?: React.ReactNode;
  fallback?: string; // if nothing resolvable
  as?: React.ElementType; // rendered element/component tag
  style?: React.CSSProperties;
  className?: string;
  full?: boolean; // use detailed label (road, city, region)
  drawing?: MapDrawing | null;
  geometry?: { type: string; coordinates: any } | null;
};

const GeoLabel: React.FC<Props> = ({
  lat,
  lng,
  initialText,
  icon,
  fallback = 'Location',
  as = 'span',
  style,
  className,
  full = false,
  drawing = null,
  geometry = null
}) => {
  const { getLabel, getFullLabel, getLabelSync, getFullLabelSync } = useReverseGeocode();

  const resolveSinglePoint = React.useCallback(async (
    useFull: boolean
  ): Promise<string> => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return '';
    const sync = useFull ? getFullLabelSync : getLabelSync;
    const asyncFn = useFull ? getFullLabel : getLabel;
    const cached = sync(lat, lng);
    if (cached) return cached;
    return asyncFn(lat, lng);
  }, [lat, lng, getFullLabel, getLabel, getFullLabelSync, getLabelSync]);

  const resolvedGeometry = React.useMemo(() => {
    if (geometry) return geometry;
    if (drawing?.geometry) {
      try {
        const parsed = JSON.parse(drawing.geometry);
        if (parsed && parsed.coordinates) {
          return parsed;
        }
      } catch (err) {
        
      }
    }
    return null;
  }, [geometry, drawing]);

  const resolveFromGeometry = React.useCallback(async (
    geom: { type?: string; coordinates?: any } | null,
    useFull: boolean
  ): Promise<string> => {
    if (!geom) return '';

    const type = typeof geom.type === 'string' ? geom.type.toLowerCase() : '';
    let coords: number[][] = [];
    if (type === 'point') {
      coords = [geom.coordinates];
    } else if (type === 'linestring') {
      coords = Array.isArray(geom.coordinates) ? geom.coordinates : [];
    } else if (type === 'polygon') {
      coords = Array.isArray(geom.coordinates?.[0]) ? geom.coordinates[0] : [];
    }

    if (!Array.isArray(coords) || coords.length === 0) {
      return '';
    }

    const deduped: number[][] = [];
    const seen: Record<string, true> = {};
    coords.forEach((pair: any) => {
      if (!Array.isArray(pair) || pair.length < 2) return;
      const lngCoord = Number(pair[0]);
      const latCoord = Number(pair[1]);
      if (!Number.isFinite(latCoord) || !Number.isFinite(lngCoord)) return;
      const key = `${lngCoord}:${latCoord}`;
      if (!seen[key]) {
        seen[key] = true;
        deduped.push([lngCoord, latCoord]);
      }
    });

    if (!deduped.length) {
      return '';
    }

    let minLatIndex = -1;
    let maxLatIndex = -1;
    let minLngIndex = -1;
    let maxLngIndex = -1;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    deduped.forEach((pair, index) => {
      const lngCoord = pair[0];
      const latCoord = pair[1];
      if (latCoord < minLat) { minLat = latCoord; minLatIndex = index; }
      if (latCoord > maxLat) { maxLat = latCoord; maxLatIndex = index; }
      if (lngCoord < minLng) { minLng = lngCoord; minLngIndex = index; }
      if (lngCoord > maxLng) { maxLng = lngCoord; maxLngIndex = index; }
    });

    const used: Record<number, true> = {};
    const sample: number[][] = [];
    const addIndex = (idx: number) => {
      if (idx >= 0 && !used[idx]) {
        used[idx] = true;
        sample.push(deduped[idx]);
      }
    };
    addIndex(minLatIndex);
    addIndex(maxLatIndex);
    addIndex(minLngIndex);
    addIndex(maxLngIndex);

    if (!sample.length) {
      sample.push(deduped[0]);
    }

    const asyncFn = useFull ? getFullLabel : getLabel;
    const syncFn = useFull ? getFullLabelSync : getLabelSync;

    const labels = await Promise.all(
      sample.map(async (pair) => {
        const lngCoord = pair[0];
        const latCoord = pair[1];
        const cached = syncFn(latCoord, lngCoord);
        if (cached) return cached;
        return asyncFn(latCoord, lngCoord);
      })
    );

    const validLabels = labels.filter((entry): entry is string => Boolean(entry));
    if (!validLabels.length) {
      return '';
    }

    const splitLabels = validLabels.map(label => label.split(',').map(part => part.trim()).filter(Boolean));
    if (!splitLabels.length) {
      return validLabels[0];
    }

    const maxDepth = Math.max.apply(null, splitLabels.map(parts => parts.length));
    const commonParts: string[] = [];
    for (let depth = 1; depth <= maxDepth; depth++) {
      let referenceValue: string | null = null;
      let valid = true;
      for (let i = 0; i < splitLabels.length; i++) {
        const parts = splitLabels[i];
        const idx = parts.length - depth;
        if (idx < 0) {
          valid = false;
          break;
        }
        const value = parts[idx];
        if (!value) {
          valid = false;
          break;
        }
        if (referenceValue === null) {
          referenceValue = value;
        } else if (referenceValue !== value) {
          valid = false;
          break;
        }
      }
      if (!valid || referenceValue === null) {
        break;
      }
      commonParts.unshift(referenceValue);
    }

    if (commonParts.length) {
      return commonParts.join(', ');
    }

    const countryValues: string[] = [];
    for (let i = 0; i < splitLabels.length; i++) {
      const parts = splitLabels[i];
      const value = parts[parts.length - 1];
      if (value) {
        if (countryValues.indexOf(value) === -1) {
          countryValues.push(value);
        }
      }
    }
    if (countryValues.length === 1) {
      return countryValues[0];
    }
    if (countryValues.length > 1) {
      return countryValues.join(' / ');
    }

    return 'Multiple locations';
  }, [getFullLabel, getLabel, getFullLabelSync, getLabelSync]);

  const evaluateLabel = React.useCallback(async (useFull: boolean) => {
    if (resolvedGeometry) {
      const value = await resolveFromGeometry(resolvedGeometry, useFull);
      if (value) return value;
    }
    return resolveSinglePoint(useFull);
  }, [resolvedGeometry, resolveFromGeometry, resolveSinglePoint]);

  const [text, setText] = React.useState<string>(initialText || '');

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (initialText) {
        setText(initialText);
      }
      const result = await evaluateLabel(full);
      if (mounted && result && result !== initialText) {
        setText(result);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [initialText, evaluateLabel, full]);

  const Tag = as as any;
  const label = text || initialText || fallback;
  const iconNode = icon !== undefined ? icon : <PinIcon size={12} />;
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    ...style
  };

  return (
    <Tag style={baseStyle} className={className}>
      {iconNode}
      <span>{label}</span>
    </Tag>
  );
};

export default GeoLabel;
